import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { usersService } from '../services/usersService';
import { User, UserRole } from '../types';
import { toast } from 'react-toastify';
import { RootState } from '../store/store';
import { UsersDetailModal } from '../components/UsersDetailModal';

const UsersPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterActive, setFilterActive] = useState<string>('all');

  // Detail modal state
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Only ORG_ADMIN and PLATFORM_ADMIN can access this page
  const canManageUsers = currentUser?.role === UserRole.ORG_ADMIN || currentUser?.role === UserRole.PLATFORM_ADMIN;

  const loadUsers = async () => {
    try {
      setLoading(true);
      const isActiveFilter =
        filterActive === 'all'
          ? undefined
          : filterActive === 'active';

      const data = await usersService.getAll({ isActive: isActiveFilter });
      setUsers(data);
    } catch (error: any) {
      console.error('Failed to load users:', error);
      toast.error(t('users.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterActive]);

  const handleRowClick = (user: User) => {
    setSelectedUser(user);
    setIsDetailModalOpen(true);
  };

  const handleEdit = () => {
    if (selectedUser) {
      navigate(`/app/users/${selectedUser.id}`);
    }
  };

  const getRoleBadgeColor = (role: UserRole) => {
    switch (role) {
      case UserRole.PLATFORM_ADMIN:
        return 'bg-purple-100 text-purple-800';
      case UserRole.ORG_ADMIN:
        return 'bg-blue-100 text-blue-800';
      case UserRole.MANAGER:
        return 'bg-green-100 text-green-800';
      case UserRole.STAFF:
        return 'bg-gray-100 text-gray-800';
      case UserRole.PROVIDER_USER:
      case UserRole.PROVIDER_MANAGER:
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleLabel = (role: UserRole) => {
    switch (role) {
      case UserRole.PLATFORM_ADMIN:
        return t('users.roles.platformAdmin');
      case UserRole.ORG_ADMIN:
        return t('users.roles.orgAdmin');
      case UserRole.MANAGER:
        return t('users.roles.manager');
      case UserRole.STAFF:
        return t('users.roles.staff');
      case UserRole.PROVIDER_USER:
        return t('users.roles.providerUser');
      case UserRole.PROVIDER_MANAGER:
        return t('users.roles.providerManager');
      default:
        return role;
    }
  };

  // Get the entity name (Provider or Customer) for the user
  const getEntityBadge = (user: User) => {
    if (user.provider) {
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-orange-50 text-orange-700 border border-orange-200">
          {user.provider.name}
        </span>
      );
    }
    return null;
  };

  if (!canManageUsers) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <p className="text-red-500 dark:text-red-400 text-lg">{t('users.noPermission')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {t('users.title')}
          </h1>
          <button
            onClick={() => navigate('/app/users/new')}
            className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors min-h-[44px] font-medium"
          >
            + {t('users.addUser')}
          </button>
        </div>

        <div className="flex gap-4 mb-4 sm:mb-6">
          <div className="flex-1 sm:flex-none">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('common.status')}
            </label>
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="w-full sm:w-auto px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white min-h-[44px]"
            >
              <option value="all">{t('common.all')}</option>
              <option value="active">{t('common.active')}</option>
              <option value="inactive">{t('common.inactive')}</option>
            </select>
          </div>
        </div>
      </div>

      {users.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p className="text-gray-500 dark:text-gray-400 text-lg">
            {t('users.noUsers')}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {users.map((user) => (
              <li key={user.id}>
                <div
                  onClick={() => handleRowClick(user)}
                  className="px-4 py-4 sm:px-6 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="text-base sm:text-lg font-medium text-blue-600 dark:text-blue-400 hover:underline">
                          {user.fullName}
                        </h3>
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                          {getRoleLabel(user.role)}
                        </span>
                        {getEntityBadge(user)}
                        {!user.isActive && (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            {t('common.inactive')}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <div className="truncate">
                          <span className="font-medium">{t('users.email')}:</span>{' '}
                          {user.email}
                        </div>
                        <div>
                          <span className="font-medium">{t('users.created')}:</span>{' '}
                          {new Date(user.createdAt).toLocaleDateString()}
                        </div>
                        <div>
                          <span className="font-medium">{t('users.lastLogin')}:</span>{' '}
                          {user.lastLoginAt
                            ? new Date(user.lastLoginAt).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                            : '-'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Detail Modal */}
      <UsersDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        user={selectedUser}
        onEdit={handleEdit}
        canManage={canManageUsers}
      />
    </div>
  );
};

export default UsersPage;
